import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from './ProgressBar';
import { formatDateTimeFR } from '../../utils/formatFR';
import Step1_Taxonomy from './steps/Step1_Taxonomy';
import Step2_PackType from './steps/Step2_PackType';
import Step3_KeyInfo from './steps/Step3_KeyInfo';
import Step4_Owner from './steps/Step4_Owner';
import Step5_MandateMedia from './steps/Step5_MandateMedia';
import Step6_Summary from './steps/Step6_Summary';
import ImportMandateModal from './ImportMandateModal';
import { saveWizardDraft, loadWizardDraft, clearWizardDraft } from './wizardDraftStorage';
import { peutAvancerVers, champsManquants } from './reglesEtapes';
import ChampsManquants from './ChampsManquants';
import { calculateWizardCompletion } from './completionScore';

const DRAFT_VERSION = 2; // v2 = sauvegarde IndexedDB avec médias

// Inclut photos/documents (stockés dans IndexedDB — quota bien plus large que localStorage)
const serializeDraft = (wizardData, currentStep) => ({
  version: DRAFT_VERSION,
  savedAt: new Date().toISOString(),
  currentStep,
  data: wizardData,
});

const PropertyCreationWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showCancelModal, setShowCancelModal] = useState(false);
  // lot CW — même règle que la liste des biens : < 768 px, l'affichage change (⛔ pas de
  // duplication de `data-testid` : chaque élément déplacé n'est rendu qu'une fois).
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [draftInfo, setDraftInfo] = useState(null); // { savedAt, currentStep }
  const [showSavedToast, setShowSavedToast] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showMandateImport, setShowMandateImport] = useState(false);
  const [showAiToast, setShowAiToast] = useState(null);
  const [etapeRefusee, setEtapeRefusee] = useState(null);
  const saveTimerRef = useRef(null);
  const hasInteractedRef = useRef(false);

  // Vérifier l'authentification au chargement en validant le token côté serveur
  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        // Vérifier que le token est encore valide
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/negotiators`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          if (res.status === 401 || res.status === 403) {
            throw new Error('Token invalide');
          }
        });
        setIsAuthenticated(true);
        // Une fois authentifié, vérifier s'il y a un brouillon à reprendre
        try {
          const draft = await loadWizardDraft();
          if (draft?.data && (draft.version === DRAFT_VERSION || draft.version === 1)) {
            setDraftInfo({ savedAt: draft.savedAt, currentStep: draft.currentStep || 1 });
            setShowResumeModal(true);
          }
        } catch {
          // brouillon corrompu, on ignore (nettoyage silencieux)
          await clearWizardDraft();
        }
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } finally {
        setCheckingAuth(false);
      }
    };
    validateToken();
  }, [navigate]);

  const [wizardData, setWizardData] = useState({
    // Étape 1 - Taxonomie
    operation: '',
    objet: '',
    profil: '',
    sous_profil: '',
    cible: '',
    
    // Étape 2 - Pack
    is_pack: false,
    pack_type: '',
    bundle_mode: 'ensemble',
    
    // Étape 3 - Infos clés
    titre_annonce: '',
    ville: '',
    code_postal: '',
    adresse: '',
    secteur: '',
    prix: '',
    loyer: '',
    surface_totale_m2: '',
    statut_bien: 'draft',
    charges: '',
    honoraires: '',
    honoraires_charge: '',  // 'vendeur' | 'acquereur' | ''  (partagés non géré)
    honoraires_pct: '',  // pourcentage si exprimé en %
    description_courte: '',
    description_detaillee: '',  // Description détaillée IA (fiche_technique / Français)
    
    // Étape 4 - Propriétaire
    contact_proprietaire_id: '',
    nouveau_contact: {
      civilite: '',
      nom: '',
      prenom: '',
      telephone: '',
      email: '',
      // lot Z1 — même vocabulaire que OwnerPersonForm/DEFAULT_OWNER_VALUE : la valeur
      // héritée `particulier` faisait ouvrir le formulaire SOCIÉTÉ à un particulier.
      type: 'personne_physique',
      societe: '',
      ville: '',
      adresse: '',
      code_postal: '',
      pays: 'France'
    },
    
    // Étape 5 - Mandat & Médias
    numero_mandat: '',
    negociateur: '',
    date_mandat: '',
    photos: [],
    documents: []
  });

  const updateWizardData = (newData) => {
    hasInteractedRef.current = true;
    setWizardData(prev => ({ ...prev, ...newData }));
  };

  // Importer depuis un mandat IA — applique les champs extraits dans wizardData
  const applyMandateExtraction = (extracted) => {
    hasInteractedRef.current = true;
    const validOps = ['cession', 'vente_immo', 'location', 'location_droit_entree', 'location_gerance'];
    const validCibles = ['investisseur', 'utilisateur', 'indifferent'];
    setWizardData(prev => {
      const operation = validOps.includes(extracted.operation) ? extracted.operation : prev.operation;
      const isLocDE = operation === 'location_droit_entree';
      const isPrixOp = operation === 'cession' || operation === 'vente_immo';

      return {
        ...prev,
        // Étape 1 — Taxonomie (les enums objet/profil/sous_profil sont validés côté wizard via taxonomyConfig)
        operation,
        objet: extracted.objet || prev.objet,
        profil: extracted.profil || prev.profil,
        sous_profil: extracted.sous_profil || prev.sous_profil,
        cible: validCibles.includes(extracted.cible) ? extracted.cible : prev.cible,
        // Étape 3 — Infos clés
        titre_annonce: extracted.titre_annonce || prev.titre_annonce,
        ville: extracted.ville || prev.ville,
        code_postal: extracted.code_postal || prev.code_postal,
        adresse: extracted.adresse || prev.adresse,
        secteur: extracted.secteur || prev.secteur,
        // Cas 1 : location_droit_entree → droit_entree + loyer_mensuel
        ...(isLocDE && extracted.droit_entree != null ? { droit_entree: String(extracted.droit_entree) } : {}),
        ...(isLocDE && extracted.loyer_mensuel != null ? { loyer_mensuel: String(extracted.loyer_mensuel) } : (isLocDE && extracted.loyer != null ? { loyer_mensuel: String(extracted.loyer) } : {})),
        // Cas 2 : cession / vente_immo → prix
        ...(isPrixOp && extracted.prix != null ? { prix: String(extracted.prix) } : {}),
        // Cas 3 : location classique → loyer
        ...(!isLocDE && !isPrixOp && extracted.loyer != null ? { loyer: String(extracted.loyer) } : {}),
        // Surfaces et charges
        surface_totale_m2: extracted.surface_totale_m2 != null ? String(extracted.surface_totale_m2) : prev.surface_totale_m2,
        charges: extracted.charges != null ? String(extracted.charges) : prev.charges,
        honoraires: extracted.honoraires != null ? String(extracted.honoraires) : prev.honoraires,
        honoraires_pct: extracted.honoraires_pct != null ? String(extracted.honoraires_pct) : prev.honoraires_pct,
        honoraires_charge: ['vendeur', 'acquereur'].includes(extracted.honoraires_charge) ? extracted.honoraires_charge : prev.honoraires_charge,
        description_courte: extracted.description_courte || prev.description_courte,
        // Étape 4 — Propriétaire
        nouveau_contact: {
          ...prev.nouveau_contact,
          ...(extracted.nouveau_contact || {}),
        },
        // Étape 5 — Mandat
        numero_mandat: extracted.numero_mandat || prev.numero_mandat,
        date_mandat: extracted.date_mandat || prev.date_mandat,
      };
    });
    setShowAiToast(`✓ ${extracted.extracted_metadata?.fields_found || 0} champs pré-remplis par l'IA. Vérifiez chaque étape avant de valider.`);
    setTimeout(() => setShowAiToast(null), 5000);
    // Aller à l'étape 3 (Infos clés) qui est la plus visuelle pour confirmer l'effet
    setCurrentStep(3);
  };

  // ─── Auto-save IndexedDB (debounce 800ms) ────────────────
  useEffect(() => {
    // Ne pas sauvegarder tant que l'auth n'est pas validée ou que le user n'a rien modifié
    if (!isAuthenticated || !hasInteractedRef.current || showResumeModal) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const payload = serializeDraft(wizardData, currentStep);
      const ok = await saveWizardDraft(payload);
      if (ok) {
        setShowSavedToast(true);
        setTimeout(() => setShowSavedToast(false), 1500);
      }
    }, 800);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [wizardData, currentStep, isAuthenticated, showResumeModal]);

  // Vider le brouillon (utilisé après création réussie ou annulation)
  const clearDraft = async () => {
    await clearWizardDraft();
  };

  // Reprendre un brouillon existant
  const resumeDraft = async () => {
    try {
      const draft = await loadWizardDraft();
      if (draft?.data) {
        // Restaurer en conservant les structures par défaut (fallback si draft v1 sans médias)
        setWizardData(prev => ({
          ...prev,
          ...draft.data,
          nouveau_contact: { ...prev.nouveau_contact, ...(draft.data.nouveau_contact || {}) },
          photos: Array.isArray(draft.data.photos) ? draft.data.photos : [],
          documents: Array.isArray(draft.data.documents) ? draft.data.documents : [],
        }));
        setCurrentStep(draft.currentStep || 1);
      }
    } catch (e) {
      console.warn('[Wizard] resume draft failed:', e);
    }
    setShowResumeModal(false);
  };

  // Jeter le brouillon et repartir de zéro
  const discardDraft = async () => {
    await clearDraft();
    setDraftInfo(null);
    setShowResumeModal(false);
  };

  // lot Z1 — règle UNIQUE, définie dans reglesEtapes.js et partagée avec les étapes.
  // Ne réécrivez pas la condition ici : c'est cette duplication qui empêchait de créer
  // un bien avec un propriétaire personne morale.
  const canProceedToStep = (step) => peutAvancerVers(step, wizardData);

  // lot Z2 — étape dont le passage a été refusé ; la liste des champs manquants est
  // recalculée en direct, elle disparaît donc dès que l'utilisateur complète sa saisie.
  const champsBloquants = etapeRefusee ? champsManquants(etapeRefusee, wizardData) : [];

  const nextStep = () => {
    if (canProceedToStep(currentStep + 1)) {
      setEtapeRefusee(null);
      setCurrentStep(prev => Math.min(prev + 1, 6));
    } else {
      setEtapeRefusee(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Navigation directe via clic sur une étape du bandeau de progression
  // - Autorise toujours le retour vers une étape passée ou courante
  // - N'autorise le saut en avant que si toutes les étapes intermédiaires sont valides
  const goToStep = (target) => {
    if (target === currentStep) return;
    if (target < currentStep) {
      setCurrentStep(target);
      return;
    }
    // Avant : vérifier la chaîne de prérequis
    for (let s = currentStep + 1; s <= target; s += 1) {
      if (!canProceedToStep(s)) {
        setEtapeRefusee(s);
        return;
      }
    }
    setEtapeRefusee(null);
    setCurrentStep(target);
  };

  const steps = [
    { number: 1, name: 'Classification', component: Step1_Taxonomy },
    { number: 2, name: 'Type de dossier', component: Step2_PackType },
    { number: 3, name: 'Informations clés', component: Step3_KeyInfo },
    { number: 4, name: 'Propriétaire', component: Step4_Owner },
    { number: 5, name: 'Mandat & Médias', component: Step5_MandateMedia },
    { number: 6, name: 'Récapitulatif', component: Step6_Summary }
  ];

  const CurrentStepComponent = steps[currentStep - 1].component;

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const confirmCancel = () => {
    navigate('/properties');
  };

  // Afficher un loader pendant la vérification d'auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="bi bi-arrow-repeat animate-spin text-4xl text-blue-600"></i>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Ne pas afficher si pas authentifié
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 text-left overflow-x-hidden champs-16-mobile">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Nouveau bien</h1>
              <p className="text-sm text-gray-600 mt-1 hidden md:block">Créez un bien en quelques étapes simples</p>
            </div>
            <div className="flex items-center gap-3">
              {currentStep === 1 && (
                <button
                  onClick={() => setShowMandateImport(true)}
                  className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-700 hover:from-violet-700 hover:to-indigo-800 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
                  data-testid="open-import-mandate-btn"
                  title="Coller un mandat et laisser l'IA pré-remplir le formulaire"
                >
                  <i className="bi bi-stars"></i>
                  Importer depuis un mandat (IA)
                </button>
              )}
              <button
                onClick={handleCancel}
                className="hidden md:block px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCancel}
                aria-label="Quitter la création"
                className="text-gray-600 hover:text-gray-900"
              >
                <i className="bi bi-x-lg text-xl"></i>
              </button>
            </div>
          </div>
          
          {/* Progress Bar avec navigation rapide */}
          <div className="mt-4 md:mt-6 flex items-center gap-3">
            {/* Bouton Précédent rapide */}
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              data-testid="wizard-top-prev"
              title="Étape précédente"
              className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i className="bi bi-chevron-left"></i>
            </button>

            {/* lot CW — sur téléphone, les 6 ronds débordaient de l'écran : la page défilait
                horizontalement et les fenêtres fixes se centraient de travers. On garde les
                deux flèches (mêmes testids) et on résume l'avancement au centre. */}
            {isMobile ? (
              <div className="flex-1 min-w-0 flex items-center justify-between gap-2"
                   data-testid="wizard-etape-mobile">
                <span className="text-sm text-gray-600">
                  Étape {currentStep} sur {steps.length} ·{' '}
                  <span className="text-blue-600 font-bold">{steps[currentStep - 1].name}</span>
                </span>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  Complété {calculateWizardCompletion(wizardData)} %
                </span>
              </div>
            ) : (
            <div className="flex-1 flex items-center justify-between">
              {steps.map((step, idx) => {
                const isActive = currentStep === step.number;
                const isDone = currentStep > step.number;
                return (
                  <React.Fragment key={step.number}>
                    <button
                      type="button"
                      onClick={() => goToStep(step.number)}
                      data-testid={`wizard-step-dot-${step.number}`}
                      className="flex flex-col items-center group focus:outline-none"
                      title={`Aller à l'étape ${step.number} — ${step.name}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-transform group-hover:scale-110 ${
                        isDone ? 'bg-green-500 text-white' :
                        isActive ? 'bg-blue-600 text-white' :
                        'bg-gray-200 text-gray-500 group-hover:bg-gray-300'
                      }`}>
                        {isDone ? <i className="bi bi-check-lg"></i> : step.number}
                      </div>
                      <span className={`text-xs mt-2 text-center transition-colors ${
                        isActive ? 'text-blue-600 font-semibold' : 'text-gray-500 group-hover:text-gray-700'
                      }`}>
                        {step.name}
                      </span>
                    </button>
                    {idx < steps.length - 1 && (
                      <div className={`flex-1 h-1 mx-2 rounded ${
                        currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            )}

            {/* Bouton Suivant rapide */}
            <button
              onClick={nextStep}
              disabled={currentStep === 6 || !canProceedToStep(currentStep + 1)}
              data-testid="wizard-top-next"
              title={
                currentStep === 6 || canProceedToStep(currentStep + 1)
                  ? 'Étape suivante'
                  : `Renseignez : ${champsManquants(currentStep + 1, wizardData).join(', ')}`
              }
              className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>

          {/* lot CW — barre de 6 segments + prochaine étape, uniquement sur téléphone. */}
          {isMobile && (
            <>
              <div className="mt-3 flex items-center gap-1.5" data-testid="wizard-segments-mobile">
                {steps.map((step) => (
                  <div key={step.number}
                       className={`h-1.5 flex-1 rounded-full ${
                         currentStep >= step.number ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
                ))}
              </div>
              {currentStep < steps.length && (
                <p className="mt-2 text-xs text-gray-500">
                  Ensuite : {steps[currentStep].name}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-3 md:px-6 lg:px-8 py-4 md:py-8">
        <div className="bg-white rounded-lg shadow-md p-4 md:p-8">
          <ChampsManquants champs={champsBloquants} variant="banner" />
          <CurrentStepComponent
            data={wizardData}
            updateData={updateWizardData}
            nextStep={nextStep}
            prevStep={prevStep}
            currentStep={currentStep}
            clearDraft={clearDraft}
          />
        </div>
      </div>

      {/* Toast « Brouillon sauvegardé » */}
      {showSavedToast && (
        <div
          data-testid="wizard-saved-toast"
          className="fixed bottom-above-mobile-nav md:bottom-6 right-6 bg-slate-900 text-white px-4 py-2.5 rounded-lg shadow-lg text-sm flex items-center gap-2 z-50 animate-fade-in"
        >
          <i className="bi bi-cloud-check-fill text-green-400"></i>
          Brouillon sauvegardé
        </div>
      )}

      {/* Modale « Reprendre le brouillon ? » */}
      {showResumeModal && draftInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="wizard-resume-modal">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <i className="bi bi-clock-history text-blue-600 text-3xl"></i>
              <h3 className="text-xl font-bold">Reprendre votre brouillon ?</h3>
            </div>

            <p className="text-gray-600 mb-2">
              Un bien en cours de saisie a été détecté.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Sauvegardé le <span className="num">{formatDateTimeFR(draftInfo.savedAt)}</span> — étape {draftInfo.currentStep} sur 6.
            </p>

            <div className="flex gap-3">
              <button
                onClick={discardDraft}
                className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
                data-testid="wizard-discard-draft"
              >
                Nouveau bien
              </button>
              <button
                onClick={resumeDraft}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                data-testid="wizard-resume-draft"
              >
                Reprendre
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale de confirmation d'annulation
          MOBILE (< sm) : padding + wrapping resserrés + boutons empilés → le contenu
          tient dans un viewport 390 px sans débordement ni troncature. */}
      {showCancelModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          data-testid="wizard-cancel-modal"
        >
          <div
            className="bg-white rounded-lg p-5 sm:p-8 shadow-xl w-full max-w-md"
            style={{ maxWidth: 'min(28rem, calc(100vw - 2rem))' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <i className="bi bi-exclamation-circle-fill text-orange-500 text-3xl shrink-0"></i>
              <h3 className="text-lg sm:text-xl font-bold break-words">Quitter la création ?</h3>
            </div>

            <p className="text-sm sm:text-base text-gray-600 mb-6 break-words">
              Votre brouillon est conservé sur cet appareil, photos et documents compris. Vous le
              retrouverez en cliquant de nouveau sur «&nbsp;Nouveau bien&nbsp;».
            </p>

            {/* lot CW — ⛔ plus de rouge : quitter ne supprime rien. Le bleu va à l'action qui
                garde l'utilisateur dans sa saisie ; sur téléphone, il est en haut. */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                data-testid="wizard-cancel-modal-keep"
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm sm:text-base"
              >
                Continuer la création
              </button>
              <button
                onClick={confirmCancel}
                data-testid="wizard-cancel-modal-quit"
                className="flex-1 px-4 py-3 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-lg transition-colors text-sm sm:text-base"
              >
                Quitter et garder le brouillon
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modale d'import depuis mandat IA */}
      <ImportMandateModal
        open={showMandateImport}
        onClose={() => setShowMandateImport(false)}
        onApply={applyMandateExtraction}
      />

      {/* Toast IA pré-remplissage */}
      {showAiToast && (
        <div
          className="fixed top-6 right-6 z-[150] bg-emerald-600 text-white px-4 py-3 rounded-lg shadow-2xl text-sm font-medium max-w-md"
          data-testid="ai-prefill-toast"
        >
          <i className="bi bi-stars mr-2"></i>{showAiToast}
        </div>
      )}
    </div>
  );
};

export default PropertyCreationWizard;
